import { generatePath } from 'react-router-dom';

import { UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { AttributeType } from 'api/attributes/requests';
import { useCompleteOnboardingStep } from 'api/company/hooks';
import { useInvalidateFavorites } from 'api/favorites/hooks';
import { waitForElasticRefresh } from 'api/hooks';
import { useInvalidateJourneyActionForms } from 'api/journey-action-forms/hooks';
import { handleError } from 'api/query-client';
import {
  JourneyAction,
  JourneyType,
  PublishedVersion,
  UnpublishedChanges,
  VersionComparison,
} from 'api/types';
import { routePaths } from 'ui/authenticated/navigation';
import { isInTemplateRoute } from 'ui/router';

import {
  addJourneyTemplate,
  cancelJourneyChanges,
  compareWithCurrent,
  createAttribute,
  createJourney,
  deleteJourney,
  duplicateJourney,
  DuplicateJourneyData,
  editJourney,
  fetchVersionPreview,
  JourneyAttributeData,
  JourneySettings,
  ActionPreviewLiveContent,
  loadActionEmailTemplate,
  loadActionSmsTemplate,
  previewActionEmailLive,
  previewActionSmsLive,
  loadJourney,
  loadJourneyAttributes,
  loadJourneyIssues,
  loadJourneyOverview,
  loadJourneys,
  loadTemplateJourney,
  loadTemplateJourneyAttributes,
  loadPublishedVersions,
  loadPublishedVersionsCount,
  loadUnpublishedChanges,
  moveJourney,
  PaginatedJourneysResponse,
  publishJourney,
  removeAttribute,
  restoreToVersion,
  updateAttribute,
  updateAttributeOrders,
  updateJourneyCollapse,
  updateJourneySettings,
  updateVersionName,
} from './requests';

const getJourneysQueryKey = (queryString?: string) => ['journeys', queryString].filter(Boolean);

export const useJourneys = (queryString: string, disabled?: boolean) => {
  const query = useQuery<PaginatedJourneysResponse | JourneyType[], AxiosError, JourneyType[]>({
    queryKey: getJourneysQueryKey(queryString),
    queryFn: () => loadJourneys(queryString),
    keepPreviousData: true,
    enabled: !disabled,
    select: (data) => {
      return Array.isArray(data) ? data : data?.results ?? [];
    },
  });
  return query;
};

export const useCreateJourney = (
  options?: UseMutationOptions<{ shortId: string }, any, { title: string; workspace: string }>
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneys } = useInvalidateJourneys();
  return useMutation({
    ...options,
    mutationFn: (data) => createJourney(data),
    onSuccess: (...data) => {
      invalidateJourneys();
      options?.onSuccess?.(...data);
    },
    onError: (...args) => {
      handleError(t('journeyNotCreated'));
      options?.onError?.(...args);
    },
  });
};

export const getJourneyQueryKey = (journeyId?: string) => ['journey', journeyId].filter(Boolean);
const getJourneyAttributesQueryKey = (
  journeyId?: string,
  params?: Record<string, string | number | boolean>
) => ['journey-attributes', journeyId, params].filter(Boolean);

const getJourneyIssuesQueryKey = (journeyId?: string) =>
  ['journey-issues', journeyId].filter(Boolean);

export const useJourneyCached = (journeyId: JourneyType['shortId']) => {
  const queryKey = getJourneyQueryKey(journeyId);
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData(queryKey) as ReturnType<typeof useJourney>['data'];
  return data;
};

export const useUpdateJourneyUserCollapse = (journeyId: JourneyType['shortId']) => {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateUserCollapse(journeyId);

  const update = (collapsedActions: Iterable<JourneyAction['shortId']>) => {
    const queryKey = getJourneyQueryKey(journeyId);
    const newActions = [...collapsedActions];
    const currentQueryData = queryClient.getQueryData(queryKey) as JourneyType;
    const newJourneyData = {
      ...currentQueryData,
      collapsedActions: newActions,
    };
    queryClient.setQueryData(queryKey, newJourneyData);
    updateMutation.mutate(newActions);
  };

  return update;
};

export const useJourney = (
  journeyId: JourneyType['shortId'] = '',
  options?: { onSuccess?: (data: JourneyType) => void; onError?: () => void }
) => {
  const loadFn = isInTemplateRoute() ? loadTemplateJourney : loadJourney;
  return useQuery({
    queryKey: getJourneyQueryKey(journeyId),
    queryFn: () => loadFn(journeyId),
    enabled: Boolean(journeyId),
    ...options,
  });
};

export const useAddJourneyTemplate = () => {
  return useMutation({
    mutationFn: addJourneyTemplate,
  });
};

export const useInvalidateJourneys = () => {
  const queryClient = useQueryClient();

  const invalidateJourneys = () => {
    return queryClient.invalidateQueries(getJourneysQueryKey());
  };
  const invalidateJourneysStats = () => {
    return queryClient.invalidateQueries(getJourneyStatsKey());
  };

  const invalidateJourney = (shortId?: JourneyType['shortId']) => {
    return Promise.all([
      queryClient.invalidateQueries(getJourneyQueryKey(shortId)),
      queryClient.invalidateQueries(getJourneyIssuesQueryKey(shortId)),
      queryClient.invalidateQueries(['unpublished-changes', shortId]),
      queryClient.invalidateQueries(['published-versions', shortId]),
      queryClient.invalidateQueries(['published-versions-count', shortId]),
    ]);
  };

  const invalidateJourneyAttributes = (shortId?: JourneyType['shortId']) => {
    return queryClient.invalidateQueries(getJourneyAttributesQueryKey(shortId));
  };

  const invalidateJourneyIssues = (shortId?: JourneyType['shortId']) => {
    return queryClient.invalidateQueries(getJourneyIssuesQueryKey(shortId));
  };

  const invalidateJourneyData = (shortId?: JourneyType['shortId']) => {
    return queryClient.invalidateQueries(getJourneyQueryKey(shortId));
  };

  return {
    invalidateJourneys,
    invalidateJourney,
    invalidateJourneyData,
    invalidateJourneysStats,
    invalidateJourneyAttributes,
    invalidateJourneyIssues,
  };
};

type CreateAttributeProps = {
  journeyShortId: string;
  data: JourneyAttributeData;
};

export const useCreateAttribute = (
  options?: UseMutationOptions<AttributeType, AxiosError, CreateAttributeProps>
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney, invalidateJourneyAttributes } = useInvalidateJourneys();
  return useMutation({
    mutationFn: ({ journeyShortId, data }: CreateAttributeProps) =>
      createAttribute(journeyShortId, data),
    ...options,
    onSuccess: (data, variables, context) => {
      invalidateJourney(variables.journeyShortId);
      invalidateJourneyAttributes(variables.journeyShortId);
      options?.onSuccess?.(data, variables, context);
    },
    onError: (_, variables) => {
      handleError(t('attributeNotSaved'), () => invalidateJourney(variables.journeyShortId));
    },
  });
};

export const useUpdateAttribute = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: updateAttribute,
    onSuccess: () => {
      invalidateJourney(journeyShortId);
    },
    onError: () => {
      handleError(t('attributeNotSaved'), () => invalidateJourney(journeyShortId));
    },
  });
};

export const useRemoveAttribute = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: (shortId: string) => removeAttribute(shortId),
    onSuccess: () => {
      invalidateJourney(journeyShortId);
    },
    onError: () => {
      handleError(t('attributeNotSaved'), () => invalidateJourney(journeyShortId));
    },
  });
};

export const useUpdateAttributeOrders = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: (data: Record<string, number>) => updateAttributeOrders(journeyShortId, data),
    onSuccess: () => {
      invalidateJourney(journeyShortId);
    },
    onError: () => {
      handleError(t('attributeOrdersNotSaved'), () => invalidateJourney(journeyShortId));
    },
  });
};

export const useEditJourney = (options?: { onSuccess?: (data: JourneyType) => void }) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateFavorites } = useInvalidateFavorites();
  return useMutation(editJourney, {
    onSuccess: (data) => {
      invalidateJourney(data.shortId);
      invalidateFavorites();
      options?.onSuccess?.(data);
    },
  });
};

export const useSaveJourneyTitle = (journey: JourneyType | undefined) => {
  const updateMutation = useEditJourney();
  return async (title: string) => {
    if (!journey || title === journey.title) return;
    await updateMutation.mutateAsync({ journeyId: journey.shortId, title });
  };
};

export const useMoveJourney = (shortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney, invalidateJourneys } = useInvalidateJourneys();
  return useMutation({
    mutationFn: (workspaceId: string) => moveJourney(shortId, workspaceId),
    onSuccess: () => {
      showSuccess(t('journeyMoved'));
      waitForElasticRefresh(() => {
        invalidateJourneys();
        invalidateJourney(shortId);
      });
    },
    onError: () => {
      handleError(t('journeyNotMoved'));
    },
  });
};

export const usePublishJourney = (shortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  const completeOnboardingStep = useCompleteOnboardingStep();
  return useMutation({
    mutationFn: (data?: { changeTravellerJourneyIds?: string[] }) => publishJourney(shortId, data),
    onSuccess: () => {
      invalidateJourney(shortId);
      completeOnboardingStep('publishJourney');
    },
    onError: () => {
      handleError(t('journeyNotPublished'));
    },
  });
};

export const getActionEmailTemplateQueryKey = (
  action: 'journey' | 'traveller',
  shortId: string,
  liveContent?: ActionPreviewLiveContent
) => [`${action}-email-template`, shortId, liveContent ?? null];

export const getActionSmsTemplateQueryKey = (
  action: 'journey' | 'traveller',
  shortId: string,
  liveContent?: ActionPreviewLiveContent
) => [`${action}-sms-template`, shortId, liveContent ?? null];

const DRAFT_PREVIEW_CACHE_TIME = 30_000;

// Pass `liveContent` (the unsaved editor body) to preview the current draft;
// omit it to preview the saved template/snapshot. The content is part of the
// query key, so edits re-fetch automatically.
export const useActionEmailTemplate = (
  action: 'journey' | 'traveller',
  shortId: string,
  liveContent?: ActionPreviewLiveContent
) => {
  return useQuery<string>({
    queryKey: getActionEmailTemplateQueryKey(action, shortId, liveContent),
    queryFn: () =>
      liveContent
        ? previewActionEmailLive(action, shortId, liveContent)
        : loadActionEmailTemplate(action, shortId),
    // Every edit changes the query key, so hold the previous render rather than
    // dropping to undefined mid-typing. Keeps `isError` observable too.
    keepPreviousData: true,
    // A draft mints a key per edit; don't retain a render each for five minutes.
    ...(liveContent ? { cacheTime: DRAFT_PREVIEW_CACHE_TIME } : {}),
  });
};

export const useActionSmsTemplate = (
  action: 'journey' | 'traveller',
  shortId: string,
  liveContent?: ActionPreviewLiveContent
) => {
  return useQuery<string>({
    queryKey: getActionSmsTemplateQueryKey(action, shortId, liveContent),
    queryFn: () =>
      liveContent
        ? previewActionSmsLive(action, shortId, liveContent)
        : loadActionSmsTemplate(action, shortId),
    // See useActionEmailTemplate.
    keepPreviousData: true,
    ...(liveContent ? { cacheTime: DRAFT_PREVIEW_CACHE_TIME } : {}),
  });
};

// The preview is server-rendered, so editing an action's content won't refresh
// it on its own -- call this after a content edit to re-fetch.
export const useInvalidateActionPreviews = () => {
  const queryClient = useQueryClient();
  return (action: 'journey' | 'traveller', shortId: string) =>
    Promise.all([
      queryClient.invalidateQueries(getActionEmailTemplateQueryKey(action, shortId)),
      queryClient.invalidateQueries(getActionSmsTemplateQueryKey(action, shortId)),
    ]);
};

export const useCancelJourneyChanges = (options?: UseMutationOptions<unknown, any, any>) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyActionForms, invalidateJourneyActionFormsList } =
    useInvalidateJourneyActionForms();
  return useMutation({
    ...options,
    mutationFn: (shortId) => cancelJourneyChanges(shortId),
    onSuccess: (...data) => {
      invalidateJourney(data[1]);
      invalidateJourneyActionForms();
      invalidateJourneyActionFormsList();
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('journeyChangesNotCanceled'));
    },
  });
};

type DeleteJourneyProps = {
  id: string;
  onSuccess?: () => void;
};
export function useDeleteJourney(props: DeleteJourneyProps) {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { invalidateJourneys } = useInvalidateJourneys();
  const { invalidateFavorites } = useInvalidateFavorites();

  return useMutation(() => deleteJourney(props.id), {
    onSuccess: () => {
      showSuccess(t('journeyDeleted'));
      queryClient.setQueriesData<PaginatedJourneysResponse | JourneyType[] | undefined>(
        { queryKey: ['journeys'] },
        (old) => {
          if (!old) return old;
          if (Array.isArray(old)) {
            return old.filter((journey) => journey.shortId !== props.id);
          }
          return {
            ...old,
            count: Math.max(0, old.count - 1),
            results: old.results.filter((journey) => journey.shortId !== props.id),
          };
        }
      );
      waitForElasticRefresh(() => invalidateJourneys());
      props.onSuccess?.();
      invalidateFavorites();
    },
    onError: () => {
      handleError(t('journeyNotDeleted'));
    },
  });
}

export const useUpdateJourneySettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: JourneySettings) => {
      const { id, ...rest } = data;
      return updateJourneySettings(id, rest);
    },
    onSuccess: (data: JourneySettings) => {
      const { id, ...rest } = data;
      queryClient.setQueryData(['journey-settings', id], rest);
    },
  });
};

export const useDuplicateJourney = (journeyId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneys, invalidateJourneysStats } = useInvalidateJourneys();

  return useMutation((data: DuplicateJourneyData) => duplicateJourney(journeyId, data), {
    onSuccess: (responseData) => {
      const duplicatedJourneyPath = generatePath(routePaths.journeyDetails, {
        workspaceId: responseData.workspace,
        journeyId: responseData.shortId,
        '*': null,
      });

      waitForElasticRefresh(() => invalidateJourneys());
      invalidateJourneysStats();
      showSuccess(responseData.title, {
        subtitle: t('journeyDuplicated'),
        url: duplicatedJourneyPath,
      });
    },
    onError: () => {
      handleError(t('journeyNotDuplicated'));
    },
  });
};

const getJourneyStatsKey = () => ['journey-stats'];

export const useJourneyAttributes = (
  journeyShortId: string,
  params?: Record<string, string | number | boolean>,
  options?: { enabled?: boolean }
) => {
  const loadFn = isInTemplateRoute() ? loadTemplateJourneyAttributes : loadJourneyAttributes;
  return useQuery({
    queryKey: getJourneyAttributesQueryKey(journeyShortId, params),
    queryFn: () => loadFn(journeyShortId, params),
    enabled: (options?.enabled ?? true) && Boolean(journeyShortId),
  });
};

export const useJourneyIssues = (journeyShortId: string) => {
  return useQuery({
    queryKey: getJourneyIssuesQueryKey(journeyShortId),
    queryFn: () => loadJourneyIssues(journeyShortId),
    enabled: Boolean(journeyShortId),
  });
};

const useUpdateUserCollapse = (journeyShortId: JourneyType['shortId']) => {
  return useMutation({
    mutationFn: (collapsedActions: Array<JourneyAction['shortId']>) =>
      updateJourneyCollapse(journeyShortId, collapsedActions),
  });
};

export const useJourneyOverview = (journeyShortId: string) => {
  return useQuery({
    queryKey: ['journey-overview', journeyShortId],
    queryFn: () => loadJourneyOverview(journeyShortId),
    enabled: Boolean(journeyShortId),
  });
};

export const usePublishedVersions = (journeyShortId: string, options?: { enabled?: boolean }) => {
  return useQuery<{ versions: PublishedVersion[] }, any, PublishedVersion[]>({
    queryKey: ['published-versions', journeyShortId],
    queryFn: () => loadPublishedVersions(journeyShortId),
    enabled: Boolean(journeyShortId) && (options?.enabled ?? true),
    select: (data) => data.versions,
  });
};

export const usePublishedVersionsCount = (journeyShortId: string) => {
  return useQuery<{ count: number }, any, number>({
    queryKey: ['published-versions-count', journeyShortId],
    queryFn: () => loadPublishedVersionsCount(journeyShortId),
    enabled: Boolean(journeyShortId),
    select: (data) => data.count,
  });
};

export const useUnpublishedChanges = (journeyShortId: string) => {
  return useQuery<UnpublishedChanges>({
    queryKey: ['unpublished-changes', journeyShortId],
    queryFn: () => loadUnpublishedChanges(journeyShortId),
    enabled: Boolean(journeyShortId),
  });
};

export const useCompareWithCurrent = (journeyShortId: string, versionId: string | null) => {
  return useQuery<VersionComparison>({
    queryKey: ['compare-with-current', journeyShortId, versionId],
    queryFn: () => compareWithCurrent(journeyShortId, versionId!),
    enabled: Boolean(journeyShortId && versionId),
  });
};

export const useRestoreVersion = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: ({ versionId, confirm }: { versionId: string; confirm: boolean }) =>
      restoreToVersion(journeyShortId, versionId, confirm),
    onSuccess: () => {
      showSuccess(t('versionRestored'));
      invalidateJourney(journeyShortId);
    },
    onError: () => {
      handleError(t('versionNotRestored'));
    },
  });
};

const getVersionPreviewQueryKey = (journeyShortId: string, versionShortId: string | null) =>
  ['version-preview', journeyShortId, versionShortId].filter(Boolean);

export const useVersionPreview = (journeyShortId: string, versionShortId: string | null) => {
  return useQuery<JourneyAction[]>({
    queryKey: getVersionPreviewQueryKey(journeyShortId, versionShortId),
    queryFn: () => fetchVersionPreview(journeyShortId, versionShortId!),
    enabled: Boolean(journeyShortId && versionShortId),
  });
};

const getPublishedVersionsQueryKey = (journeyShortId: string) => [
  'published-versions',
  journeyShortId,
];

export const useInvalidatePublishedVersions = () => {
  const queryClient = useQueryClient();

  return (journeyShortId: string) => {
    return Promise.all([
      queryClient.invalidateQueries(getPublishedVersionsQueryKey(journeyShortId)),
      queryClient.invalidateQueries(['published-versions-count', journeyShortId]),
    ]);
  };
};

export const useUpdateVersionName = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const invalidatePublishedVersions = useInvalidatePublishedVersions();

  return useMutation({
    mutationFn: ({ versionShortId, name }: { versionShortId: string; name: string | null }) =>
      updateVersionName(journeyShortId, versionShortId, name),
    onSuccess: () => {
      showSuccess(t('versionNameUpdated'));
      invalidatePublishedVersions(journeyShortId);
    },
    onError: () => {
      handleError(t('versionNameNotUpdated'));
    },
  });
};

export const locale = {
  en: {
    journeyDeleted: 'Journey deleted',
    journeyNotCreated: 'Unable to create journey',
    journeyNotDeleted: 'Journey could not be deleted',
    journeyDuplicated: 'Journey duplicated',
    journeyNotDuplicated: 'Unable to duplicate journey',
    journeyNotPublished: 'Unable to publish journey',
    journeyMoved: 'Journey moved',
    journeyNotMoved: 'Unable to move journey',
    journeyChangesNotCanceled: 'Unable to cancel changes',
    attributeNotSaved: 'Attribute could not be saved',
    attributeOrdersNotSaved: 'Attributes could not be reordered',
    versionRestored: 'Version restored',
    versionNotRestored: 'Unable to restore journey version',
    versionNameUpdated: 'Version name updated',
    versionNameNotUpdated: 'Unable to update version name',
  },
};
